import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { PopupListItem, Service } from 'webapp-types';
import { arrayMove } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import BoardLoopsGridItemComponent from 'webapp/components/board-loops/grid-item';
import BoardsIndexGridItemComponent from 'webapp/components/boards/index/grid-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import GoalsGridItemComponent from 'webapp/components/goals/grid-item';
import ReportsIndexGridItemComponent from 'webapp/components/reports/index/grid-item';
import TeamsViewSectionMetricsGridItemComponent from 'webapp/components/teams/view/section/metrics/grid-item';

import type { Board, TeamSection, TeamSectionItem } from 'webapp/models-custom';

import type { TrackingOriginTypeForPreview } from 'webapp/services/model-manager/boards';

interface Args {
	section: TeamSection;
	items: TeamSectionItem[];
}

export type Action = 'removeFromSection';
export type Option = PopupListItem<Action>;

export default class TeamsViewSectionGridComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/team-section-items')
	declare mmTeamSectionItemsService: Service.ModelManager.TeamSectionItems;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;

	visibleCheckContainerNode: HTMLElement | null = null;
	sortableEl: JQuery | null = null;

	initSortable(
		ctEl: JQuery,
		itemSelectorString: string,
		placeholderElementFn: (el: JQuery) => JQuery,
	): void {
		ctEl.sortable({
			handle: '.drag',
			containment: 'parent',
			placeholder: {
				element: placeholderElementFn,
				update: function () {
					return;
				},
			},
			forcePlaceholderSize: true,
			tolerance: 'pointer',
			items: itemSelectorString,
			start: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				ui.item.find('.drag').attr('data-position', ui.item.index());
			},
			stop: (_e: JQuery.Event, ui: jQueryUIInstance) => {
				const handle = ui.item.find('.drag'),
					oldIndex = Number(handle.attr('data-position')),
					newIndex = ui.item.index();

				if (oldIndex !== newIndex) {
					this.updateSortOrder(oldIndex, newIndex);
				}
			},
		});

		this.sortableEl = ctEl;
	}

	updateSortOrder(oldIndex: number, newIndex: number): void {
		const newItems: TeamSectionItem[] = arrayMove(this.args.items, oldIndex, newIndex),
			orderedIds = newItems.map((item) => item.numericId),
			filteredOrderedIds = orderedIds.filter((id) => id !== null);

		this.mmTeamSectionsService.updateOrder(this.args.section, filteredOrderedIds);
	}

	@action
	setupView(node: HTMLElement): void {
		this.visibleCheckContainerNode = node;

		const itemSelectorString = 'li.filter-sort-item',
			placeholderElementFn = (/*currentItem*/) => {
				return $(
					'<div class="drop-placeholder flex-item"><div><ui-icon name="DragAndDropArea" size="xl" class="bounce"></ui-icon><span>Drop here</span></div></div>',
				);
			};
		this.initSortable($(node), itemSelectorString, placeholderElementFn);

		scheduleOnce('afterRender', this, this.triggerContentChange, node);
	}

	@action
	openBoardPreview(board: Board, trackingOrigin?: string): void {
		this.mmBoardsService.openPreview(board, trackingOrigin as TrackingOriginTypeForPreview);
	}

	@action
	removeFromSection(item: TeamSectionItem): void {
		this.mmTeamSectionItemsService.delete(item);
	}

	@action
	unlockItem(item: TeamSectionItem): void {
		this.mmTeamSectionItemsService.openLimitationPopup(item);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	teardownView(): void {
		this.destroySortable();
	}

	private destroySortable(): void {
		const el = this.sortableEl;
		if (el?.length && el.sortable('instance')) {
			el.sortable('destroy');
		}
	}
	<template>
		<div class="view-grid">
			<ul
				class="flex-ct flex-ct--size-team-section-item teams-section__grid"
				{{didInsert this.setupView}}
				{{willDestroy this.teardownView}}
				{{visibleCheckContainer}}
			>
				{{#each @items as |item|}}
					{{#if item.isItemLocked}}
						<li class="flex-item teams-section__locked-item">
							<div class="teams-section__locked-item-overlay">
								<a
									role="button"
									class="button"
									{{on "click" (fn this.unlockItem item)}}
								>
									<span>
										<CommonUiIconComponent
											@name="ArrowUpCircleFill"
											@size="sm"
										/>
										Unlock
									</span>
								</a>
							</div>
						</li>
					{{else if item.boardModel}}
						<BoardsIndexGridItemComponent
							@model={{item.boardModel}}
							@onOpenPreview={{this.openBoardPreview}}
							@teamSectionItem={{item}}
							@removeFromSection={{this.removeFromSection}}
							@isRestricted={{item.isRestricted}}
							@checkVisibility={{true}}
						/>
					{{else if item.boardLoopModel}}
						<BoardLoopsGridItemComponent
							@model={{item.boardLoopModel}}
							@teamSectionItem={{item}}
							@removeFromSection={{this.removeFromSection}}
							@isRestricted={{item.isRestricted}}
							@checkVisibility={{true}}
						/>
					{{else if item.reportModel}}
						<ReportsIndexGridItemComponent
							@model={{item.reportModel}}
							@teamSectionItem={{item}}
							@removeFromSection={{this.removeFromSection}}
							@isRestricted={{item.isRestricted}}
							@checkVisibility={{true}}
						/>
					{{else if item.goalModel}}
						<GoalsGridItemComponent
							@model={{item.goalModel}}
							@teamSectionItem={{item}}
							@removeFromSection={{this.removeFromSection}}
							@isRestricted={{item.isRestricted}}
							@checkVisibility={{true}}
						/>
					{{else if item.isMetric}}
						<TeamsViewSectionMetricsGridItemComponent
							@model={{item}}
							@removeFromSection={{this.removeFromSection}}
							@isRestricted={{item.isRestricted}}
						/>
					{{/if}}
				{{/each}}

			</ul>
		</div>
	</template>
}
